// Navegação compartilhada — fonte única de NAV_ITEMS + render do shell.
//
// Antes esse array vivia DUPLICADO em 8 arquivos de página (home, map,
// roteiro, carteira, documents, settings, profile, trip-settings). Mexer
// no menu exigia editar os 8. Agora é tudo centralizado aqui.
//
// Cada página importa e usa:
//   SIDEBAR_NAV_HTML(activeId)  → <nav> da sidebar desktop
//   BOTTOM_NAV_HTML(activeId)   → <nav> do bottom nav mobile (4 abas + "Mais")
//   MORE_SHEET_HTML()           → bottom sheet "Mais" (todos os menus)
//   wireMoreSheet(root)         → listeners de abrir/fechar o sheet
//
// `activeId` é o id do item da página atual (ou null pra páginas sem aba,
// tipo settings/profile). Itens com `soon: true` aparecem com badge
// "em breve" e NÃO são clicáveis.

import { initOfflineBanner } from './offlineBanner.js';
import { initOfflineMapsLinks } from './offlineMapsLinks.js';
import { APK_DOWNLOAD_URL, isWebBrowser } from './apkDownload.js';
import { initNativeApp } from '../core/nativeApp.js';
import { initPullToRefresh } from './pullToRefresh.js';

// ─── Itens de navegação ──────────────────────────────────────────────────
// id     → identificador único (marca .active)
// label  → texto exibido
// icon   → chave em NAV_ICONS
// href   → destino (ausente quando soon)
// desc   → subtítulo usado no sheet "Mais"
// soon   → true → item "em breve", não-clicável
export const NAV_ITEMS = [
  { id: 'home',      label: 'Início',      icon: 'home',      href: '/',                 desc: 'Suas viagens' },
  { id: 'overview',  label: 'Visão geral', icon: 'compass',   href: '/visao-geral.html', desc: 'Resumo da viagem' },
  { id: 'map',       label: 'Mapa',        icon: 'map',       href: '/mapa.html',        desc: 'Rota no mapa' },
  { id: 'timeline',  label: 'Roteiro',     icon: 'timeline',  href: '/roteiro.html',     desc: 'Dia a dia da viagem' },
  { id: 'hotels',    label: 'Hotéis',      icon: 'hotel',     href: '/hoteis.html',      desc: 'Reservas e estadias' },
  { id: 'transport', label: 'Trajeto', icon: 'transport', href: '/transportes.html', desc: 'Voos e trechos' },
  { id: 'wallet',    label: 'Carteira',    icon: 'wallet',    href: '/carteira.html',    desc: 'Gastos e orçamento' },
  { id: 'docs',      label: 'Documentos',  icon: 'doc',       href: '/documents.html',   desc: 'Checklist e comprovantes' },
  { id: 'mapiwords', label: 'Mapiwords',   icon: 'game',      desc: 'Palavra do dia',       soon: true },
];

// Abas do bottom nav mobile (nessa ordem). A 5ª posição é sempre o "Mais".
const BOTTOM_TAB_IDS = ['home', 'map', 'timeline', 'wallet'];

// Seções do sheet "Mais" (mobile). Reaproveita NAV_ITEMS por id e acrescenta
// os itens de conta/perfil que não vivem na navegação principal.
function moreSections() {
  const byId = (id) => NAV_ITEMS.find((it) => it.id === id);
  return [
    {
      title: 'Viagem',
      items: [
        byId('overview'),
        byId('hotels'),
        byId('transport'),
        byId('docs'),
        { id: 'trip-settings', label: 'Configurações da viagem', icon: 'sliders', href: '/trip-settings.html', desc: 'Moeda, datas, amigos' },
      ],
    },
    {
      title: 'Diversão',
      items: [byId('mapiwords')],
    },
    // Só no navegador: dentro do app já é nativo, então escondemos.
    ...(isWebBrowser() ? [{
      title: 'App',
      items: [{
        id: 'get-app', label: 'Baixar app Android', icon: 'download',
        href: APK_DOWNLOAD_URL, desc: 'Instalar no seu celular',
      }],
    }] : []),
    {
      title: 'Você',
      items: [
        { id: 'notifications', label: 'Notificações', icon: 'bell', action: 'notif-open', desc: 'Alertas da viagem', badge: true },
        { id: 'profile',  label: 'Perfil',        icon: 'user',     href: '/profile.html',  desc: 'Viagens e países' },
        { id: 'account',  label: 'Conta',         icon: 'shield',   href: '/settings.html', desc: 'Email, senha, dados' },
        { id: 'settings', label: 'Configurações', icon: 'settings', href: '/settings.html', desc: 'Tema, moedas, sobre' },
        { id: 'logout',   label: 'Sair',          icon: 'logout',   action: 'logout', danger: true },
      ],
    },
  ];
}

// ─── Ícones (paths SVG, viewBox 0 0 24 24) ───────────────────────────────
const NAV_ICONS = {
  home:      '<path d="M3 12l9-9 9 9"/><path d="M5 10v10h14V10"/>',
  compass:   '<circle cx="12" cy="12" r="9"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" fill-opacity="0.15"/>',
  map:       '<path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z"/><path d="M9 3v15M15 6v15"/>',
  timeline:  '<circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/><path d="M12 7v10M8 12h8"/>',
  hotel:     '<path d="M2 20h20M3 20V8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12M8 10h8M8 14h8"/>',
  transport: '<rect x="4" y="3" width="16" height="13" rx="3"/><path d="M4 11h16M9 16l-2 5M15 16l2 5M9 7h6"/>',
  wallet:    '<path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5z"/><path d="M16 12h5"/><circle cx="17" cy="12" r="1" fill="currentColor"/>',
  doc:       '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h5"/>',
  game:      '<rect x="2" y="6" width="20" height="12" rx="5"/><path d="M6 12h4M8 10v4"/><circle cx="15.5" cy="11" r="1" fill="currentColor"/><circle cx="18" cy="13.5" r="1" fill="currentColor"/>',
  more:      '<path d="M4 7h16M4 12h16M4 17h16"/>',
  sliders:   '<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/>',
  user:      '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/>',
  shield:    '<path d="M12 2l9 4v6c0 5-4 9-9 10C7 21 3 17 3 12V6l9-4z"/>',
  settings:  '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9 1.65 1.65 0 0 0 4.27 7.18l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.24.58.78 1 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  logout:    '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5M21 12H9"/>',
  chevron:   '<path d="M9 6l6 6-6 6"/>',
  close:     '<path d="M18 6L6 18M6 6l12 12"/>',
  download:  '<path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/>',
  bell:      '<path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/>',
};

function navIcon(name, size = 18) {
  const paths = NAV_ICONS[name];
  if (!paths) return '';
  return `<svg class="nav-ic" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" `
    + `stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" `
    + `aria-hidden="true">${paths}</svg>`;
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ─── Sidebar (desktop) ───────────────────────────────────────────────────
/** Retorna o <nav class="home-sidebar-nav"> completo. */
export function SIDEBAR_NAV_HTML(activeId = null) {
  return `<nav class="home-sidebar-nav">${
    NAV_ITEMS.map((it) => sidebarItem(it, activeId)).join('')
  }</nav>`;
}

function sidebarItem(it, activeId) {
  if (it.soon) {
    return `<span class="sidebar-item is-soon" data-nav="${it.id}" aria-disabled="true"`
      + ` title="${esc(it.label)} — em breve">`
      + `${navIcon(it.icon, 18)}`
      + `<span class="home-sidebar-label">${esc(it.label)}</span>`
      + `<span class="nav-soon-badge">em breve</span>`
      + `</span>`;
  }
  const active = it.id === activeId ? ' active' : '';
  return `<a class="sidebar-item${active}" href="${it.href}" data-nav="${it.id}" title="${esc(it.label)}">`
    + `${navIcon(it.icon, 18)}`
    + `<span class="home-sidebar-label">${esc(it.label)}</span>`
    + `</a>`;
}

// ─── Bottom nav (mobile) ─────────────────────────────────────────────────
/** Retorna o <nav class="home-bottom-nav"> com 4 abas + botão "Mais". */
export function BOTTOM_NAV_HTML(activeId = null) {
  const tabs = BOTTOM_TAB_IDS
    .map((id) => NAV_ITEMS.find((it) => it.id === id))
    .filter(Boolean)
    .map((it) => {
      const active = it.id === activeId ? ' active' : '';
      return `<a class="home-bottom-tab${active}" href="${it.href}" data-nav="${it.id}">`
        + `<span class="home-bottom-icon">${navIcon(it.icon, 18)}</span>`
        + `<span class="home-bottom-label">${esc(it.label)}</span>`
        + `</a>`;
    }).join('');

  // "Mais" não recebe estado "ativo" pela página — ele só destaca enquanto
  // o sheet está aberto (wireMoreSheet liga/desliga a classe `active`).
  const moreBtn = `<button type="button" class="home-bottom-tab" `
    + `data-action="more-open" aria-haspopup="dialog" aria-label="Mais opções">`
    + `<span class="home-bottom-icon">${navIcon('more', 18)}</span>`
    + `<span class="home-bottom-label">Mais</span>`
    + `</button>`;

  return `<nav class="home-bottom-nav glass" role="navigation" aria-label="Principal mobile">`
    + `${tabs}${moreBtn}</nav>`;
}

// ─── Sheet "Mais" (mobile) ───────────────────────────────────────────────
/** Bottom sheet com todos os menus. Começa oculto (data-slot="more-sheet"). */
export function MORE_SHEET_HTML() {
  const sections = moreSections().map((sec) => `
    <div class="more-sheet-section">
      <p class="more-sheet-section-label">${esc(sec.title)}</p>
      <div class="more-sheet-card">${sec.items.map(moreSheetItem).join('')}</div>
    </div>
  `).join('');

  return `
    <div class="more-sheet-overlay" data-slot="more-sheet" hidden>
      <div class="more-sheet" role="dialog" aria-modal="true" aria-label="Mais opções">
        <div class="more-sheet-grip" aria-hidden="true"></div>
        <header class="more-sheet-head">
          <h2 class="more-sheet-title">Mais</h2>
          <button type="button" class="more-sheet-close" data-action="more-close" aria-label="Fechar">
            ${navIcon('close', 20)}
          </button>
        </header>
        <div class="more-sheet-body">${sections}</div>
      </div>
    </div>
  `;
}

function moreSheetItem(it) {
  if (!it) return '';
  const inner = `${navIcon(it.icon, 20)}`
    + `<span class="more-sheet-item-text">`
    +   `<span class="more-sheet-item-label">${esc(it.label)}</span>`
    +   (it.desc ? `<span class="more-sheet-item-desc">${esc(it.desc)}</span>` : '')
    + `</span>`;

  if (it.soon) {
    return `<span class="more-sheet-item is-soon" aria-disabled="true">`
      + `${inner}<span class="nav-soon-badge">em breve</span></span>`;
  }
  if (it.action === 'logout') {
    return `<button type="button" class="more-sheet-item is-danger" data-action="logout">`
      + `${navIcon(it.icon, 20)}`
      + `<span class="more-sheet-item-text"><span class="more-sheet-item-label">${esc(it.label)}</span></span>`
      + `</button>`;
  }
  // Ação genérica (ex.: abrir a central de notificações). Botão com badge opcional.
  if (it.action) {
    const badge = it.badge ? `<span class="notif-badge" data-notif-badge hidden></span>` : '';
    return `<button type="button" class="more-sheet-item" data-action="${esc(it.action)}">`
      + `${inner}${badge}<span class="more-sheet-item-chev">${navIcon('chevron', 16)}</span></button>`;
  }
  return `<a class="more-sheet-item" href="${it.href}" data-nav="${it.id}">`
    + `${inner}<span class="more-sheet-item-chev">${navIcon('chevron', 16)}</span></a>`;
}

// ─── Listeners do sheet "Mais" ───────────────────────────────────────────
/**
 * Liga os handlers de abrir/fechar do sheet "Mais". Idempotente por root
 * (não duplica listeners). Chame uma vez depois de injetar o shell.
 *
 * "Sair" usa `window.logoutConfirm` (exposto por features/auth/logout.js) —
 * a página que usa o sheet precisa importar esse módulo no seu entry.
 */
export function wireMoreSheet(root) {
  // Indicador de conectividade global: roda uma vez por página (idempotente),
  // independente do sheet "Mais". Fica aqui porque wireMoreSheet é o único
  // ponto chamado por todas as páginas que montam o shell.
  initOfflineBanner();
  // Offline: links de mapa in-app abrem o app de mapas nativo (data-maps-fallback).
  initOfflineMapsLinks();
  // App nativo: botão voltar + status bar na cor do tema (no-op na web).
  initNativeApp();
  // App nativo: pull-to-refresh (arrastar pra baixo recarrega; exceto no mapa).
  initPullToRefresh();
  // Central de notificações: sino + badge + (no app) agendamento nativo.
  // import() dinâmico pra NÃO quebrar code-splitting (regra do CLAUDE.md:
  // shared/ não importa features/ estaticamente).
  import('../features/notifications/index.js')
    .then((m) => m.initNotifications())
    .catch((e) => console.warn('[nav] notifications indisponível:', e?.message || e));
  // Registro de push (FCM): token do dispositivo salvo em users/{uid} pra
  // receber broadcasts do admin. No-op sem login.
  import('../core/pushRegister.js')
    .then((m) => m.initPushRegistration())
    .catch((e) => console.warn('[nav] push register indisponível:', e?.message || e));

  if (!root || root.__moreSheetWired) return;
  root.__moreSheetWired = true;

  const overlay = () => root.querySelector('[data-slot="more-sheet"]');

  const moreBtn = () => root.querySelector('[data-action="more-open"]');

  const open = () => {
    const ov = overlay();
    if (!ov) return;
    ov.hidden = false;
    requestAnimationFrame(() => ov.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    moreBtn()?.classList.add('active');
  };
  const close = () => {
    const ov = overlay();
    if (!ov) return;
    ov.classList.remove('is-open');
    document.body.style.overflow = '';
    moreBtn()?.classList.remove('active');
    setTimeout(() => { if (ov && !ov.classList.contains('is-open')) ov.hidden = true; }, 240);
  };

  root.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-action]');
    if (!trigger) return;
    const action = trigger.dataset.action;
    if (action === 'more-open')  { e.preventDefault(); open(); }
    if (action === 'more-close') { e.preventDefault(); close(); }
    if (action === 'logout')     { e.preventDefault(); close(); window.logoutConfirm?.(); }
    if (action === 'notif-open') { e.preventDefault(); close(); window.openNotifications?.(); }
  });

  // Clique no scrim (fora do .more-sheet) fecha.
  root.addEventListener('click', (e) => {
    const ov = overlay();
    if (ov && !ov.hidden && e.target === ov) close();
  });

  // Esc fecha.
  document.addEventListener('keydown', (e) => {
    const ov = overlay();
    if (e.key === 'Escape' && ov && !ov.hidden) close();
  });
}
